import React, { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import EmojiPicker from 'emoji-picker-react';
import { AddPhotoAlternate, Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import useDebounce from '../../hooks/debounceHook';
import api, { MEDIA_URL } from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import { formatFloatToTimeHM, DEFAULT_TIME_LOG_MINUTES } from '../../utils/timeFormatter';
import useCurrentUser, { guestCapabilities } from '../../utils/useCurrentUser';
import ImagePreviewModal from '../dialogs/imageDialog';
import ChecklistTemplateDialog from '../dialogs/ChecklistTemplateDialog';

const HalfScreenDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    alignItems: 'flex-end'
  },
  '& .MuiDialog-paper': {
    margin: 0,
    maxWidth: '50%',
    width: '50%',
    height: '100%',
    maxHeight: '100%',
    position: 'fixed',
    right: 0,
    borderRadius: 0
  }
}));

const DialogHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`
}));

const FormItem = styled(Grid)(({ theme }) => ({
  padding: theme.spacing(1),
  marginBottom: theme.spacing(1)
}));

const UploadOverlay = styled('div')({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 2
});

const DropZone = styled('div', {
  shouldForwardProp: prop => prop !== 'isDragging'
})(({ theme, isDragging }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 200,
  border: `2px dashed ${isDragging ? theme.palette.primary.main : theme.palette.primary.dark}`,
  backgroundColor: isDragging ? theme.palette.action.hover : 'transparent',
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  transition: 'border-color 120ms, background-color 120ms'
}));

const ImageGrid = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  width: '100%',
  justifyContent: 'center'
}));

const Thumbnail = styled('div')(({ theme }) => ({
  position: 'relative',
  width: 120,
  height: 120,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    cursor: 'pointer',
    display: 'block'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  right: 8,
  backgroundColor: theme.palette.primary.dark,
  color: theme.palette.common.white,
  zIndex: 3,
  padding: theme.spacing(1),
  '&:hover': {
    backgroundColor: theme.palette.primary.main
  }
}));

const CommentBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(3),
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  shadow: theme.shadows[1]
}));

const CommentInput = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2)
}));

const CommentList = styled(Box)(({ theme }) => ({
  maxHeight: '300px',
  overflowY: 'auto',
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1)
}));

const CommentItem = styled(Paper)(({ theme, isCurrentUser }) => ({
  padding: '8px 12px',
  marginBottom: '4px',
  maxWidth: '80%',
  alignSelf: isCurrentUser ? 'flex-end' : 'flex-start',
  backgroundColor: isCurrentUser
    ? theme.palette.primary.main
    : theme.palette.background.paper,
  color: isCurrentUser
    ? theme.palette.primary.contrastText
    : theme.palette.text.primary,
  borderRadius: '12px',
  position: 'relative'
}));

const CommentHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '4px'
});

const CommentContent = styled(Typography)({
  fontSize: '0.9rem',
  wordBreak: 'break-word'
});

const ReplyIndicator = styled(Box)(({ theme }) => ({
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  backgroundColor:
    theme.palette.mode === 'dark'
      ? theme.palette.grey[800]
      : theme.palette.grey[100],
  padding: '2px 8px',
  borderRadius: '4px',
  marginBottom: '4px'
}));

/**
 * @typedef {Object} ActionItemDialogProps
 * @property {boolean} open - Dialog visibility state
 * @property {number|string} actionItemId - ID of action item to display
 * @property {Function} onClose - Close handler
 */

/**
 * Action Item Dialog Component
 * @component
 * @param {ActionItemDialogProps} props
 * @returns {JSX.Element} Dialog for action item details
 */
const ActionItemDialog = ({ open, actionItemId, onClose }) => {
  const theme = useTheme();
  const [actionItem, setActionItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeLogs, setTimeLogs] = useState([]);
  const { currentWorkspace } = useWorkspace();
  const { user: profileUser } = useCurrentUser();
  const guestCaps = guestCapabilities(profileUser, currentWorkspace?.id);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const commentInputRef = useRef(null);
  const commentListRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const [checklist, setChecklist] = useState([]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [checklistTemplateDialogOpen, setChecklistTemplateDialogOpen] = useState(false);
  const handleAutoSave = useDebounce(async updatedItem => {
    if (!actionItemId || !updatedItem) return;
    try {
      await api.patch(`action-items/${actionItemId}/`, updatedItem);
    } catch (error) {
      console.error("Error autosaving:", error);
    }
  }, 500);

  // Single upload path shared by the file picker, drag-and-drop and clipboard
  // paste. Non-image files (e.g. from a mixed drop) are ignored rather than
  // rejected, so dropping a folder of mixed content still attaches the images.
  const uploadImages = async fileList => {
    const files = Array.from(fileList || []).filter(file =>
      file.type.startsWith('image/')
    );
    if (files.length === 0) return;

    setUploadingImage(true);

    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    try {
      const response = await api.post(
        `action-items/${actionItemId}/images/`,
        formData
      );
      setActionItem(prev => ({
        ...prev,
        images: [...(prev?.images || []), ...response.data]
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
      alert(`Failed to upload image: ${error.response?.data?.detail || error.response?.data?.image || error.message}`);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileChange = event => {
    uploadImages(event.target.files);
    // Reset so picking the same file again after removing it still fires change.
    event.target.value = '';
  };

  const handleDragOver = event => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = event => {
    event.preventDefault();
    setIsDragging(false);
    uploadImages(event.dataTransfer.files);
  };

  const handleDeleteImage = async imageId => {
    try {
      await api.delete(`action-item-images/${imageId}/`);
      setActionItem(prev => ({
        ...prev,
        images: (prev?.images || []).filter(image => image.id !== imageId)
      }));
    } catch (error) {
      console.error("Failed to delete image:", error);
    }
  };

  // Paste a screenshot straight into the dialog from anywhere inside it. Only
  // image entries are intercepted, so pasting text into the description or a
  // comment still behaves normally.
  useEffect(() => {
    if (!open || !actionItemId) return undefined;

    const handlePaste = event => {
      const images = Array.from(event.clipboardData?.items || [])
        .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter(Boolean);

      if (images.length === 0) return;
      event.preventDefault();
      uploadImages(images);
    };

    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, actionItemId]);

  useEffect(() => {
    const fetchActionItem = async () => {
      if (!actionItemId) return;
      try {
        const response = await api.get(`action-items/${actionItemId}/`);
        setActionItem(response.data);
      } catch (error) {
        console.error("Error fetching action item:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchTimeLogs = async () => {
      try {
        const response = await api.get(
          `/action-items/${actionItemId}/time-logs/`
        );
        setTimeLogs(response.data.payload);
      } catch (error) {
        console.error("Failed to fetch time logs", error);
      }
    };

    if (open) {
      fetchActionItem();
      fetchTimeLogs();
    }
  }, [actionItemId, open]);

  const fetchComments = async () => {
    try {
      const response = await api.get(`/action-items/${actionItemId}/comments/`);
      setComments(response.data.payload);
    } catch (error) {
      console.error("Error fetching comments:", error);
    }
  };

  useEffect(() => {
    if (actionItemId) {
      fetchComments();
      const user = localStorage.getItem('user');
      setCurrentUser(parseInt(user));
    }
  }, [actionItemId]);

  useEffect(() => {
    if (commentListRef.current) {
      commentListRef.current.scrollTop = commentListRef.current.scrollHeight;
    }
  }, [comments]);

  
  useEffect(() => {
    if (actionItemId && open) {
      fetchChecklist();
    }
  }, [actionItemId, open]);


  useEffect(() => {
    const handleClickOutside = event => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target) &&
        !emojiButtonRef.current.contains(event.target)
      ) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  /**
   * Handles comment submission for the action item.
   * Posts a new comment or reply, then refreshes the comment list.
   * @async
   * @returns {Promise<void>}
   */
  const handleCommentSubmit = async () => {
    if (!newComment.trim()) return;
    try {
      const commentData = {
        comment: newComment,
        reply_to: replyTo
      };
      await api.post(`/action-items/${actionItemId}/comments/`, commentData);
      setNewComment('');
      setReplyTo(null);
      fetchComments();
    } catch (error) {
      console.error('Error posting comment:', error);
    }
  };

  /**
   * Handles emoji selection from the emoji picker.
   * @param {Object} emojiObject - The selected emoji object.
   * @returns {void}
   */
  const onEmojiClick = emojiObject => {
    setNewComment(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
  };

  /**
   * Handles changes to the action item fields and triggers autosave.
   * @param {Object} updates - The updated fields for the action item.
   * @returns {void}
   */
  const handleActionItemChange = updates => {
    const updatedItem = { ...actionItem, ...updates };
    setActionItem(updatedItem);
    handleAutoSave(updatedItem);
  };

  if (!actionItem && !loading) return null;

  const images = actionItem?.images || [];

  /**
   * Handles changes to a time log entry, updates local state, and persists changes to the backend.
   *
   * @async
   * @function handleTimeLogChange
   * @param {number} index - The index of the time log entry being updated.
   * @param {string} field - The field of the time log being changed (e.g., 'start_time', 'end_time', 'notes').
   * @param {*} value - The new value for the specified field.
   * @returns {Promise<void>} Resolves when the update is complete.
   *
   * @description
   * - Updates the specified field of a time log entry at the given index.
   * - If the field is 'start_time' or 'end_time', recalculates the duration.
   * - Updates the local state with the new time log data.
   * - Sends a PATCH request if the time log already exists (has an id), or a POST request to create a new time log.
   * - Handles errors by logging them to the console.
   */
  const handleTimeLogChange = async (index, field, value) => {
    const logToSubmit = { ...timeLogs[index], [field]: value };
    if (field === 'start_time' || field === 'end_time') {
      // Duration is decimal hours (1h52m = 1.87); the server derives the same
      // value from the range, this just makes the cell update instantly.
      const start = dayjs(logToSubmit.start_time);
      const end = dayjs(logToSubmit.end_time);
      logToSubmit.duration =
        logToSubmit.start_time && logToSubmit.end_time && start.isValid() && end.isValid()
          ? Math.max(end.diff(start, 'minute') / 60, 0)
          : 0.0;
    }

    // Functional update so rapid edits never overwrite each other.
    const mergeRow = (patch) =>
      setTimeLogs((prev) => prev.map((log, i) => (i === index ? { ...log, ...patch } : log)));
    mergeRow(logToSubmit);

    const toIso = (v) => (v ? dayjs(v).toISOString() : null);
    try {
      if (logToSubmit.id) {
        const payload =
          field === 'start_time' || field === 'end_time'
            ? { start_time: toIso(logToSubmit.start_time), end_time: toIso(logToSubmit.end_time) }
            : { [field]: value };
        const response = await api.patch(`/time-logs/${logToSubmit.id}/`, payload);
        mergeRow({ duration: response.data.duration });
      } else if (logToSubmit.start_time || logToSubmit.end_time || logToSubmit.notes) {
        if (logToSubmit._saving) return; // a create is already in flight
        mergeRow({ _saving: true });
        const response = await api.post(`/time-logs/`, {
          action_item: actionItemId,
          start_time: toIso(logToSubmit.start_time),
          end_time: toIso(logToSubmit.end_time),
          duration: parseFloat(Number(logToSubmit.duration).toFixed(2)) || 0.0,
          notes: logToSubmit.notes,
          attendee: localStorage.getItem('user')
        });
        // Keep any edits typed while the create was in flight; take the server's id/duration.
        mergeRow({ id: response.data.id, duration: response.data.duration, _saving: false });
      }
    } catch (error) {
      console.error("Failed to save time log", error);
      mergeRow({ _saving: false });
    }
  };

  // A new row starts "now", ends DEFAULT_TIME_LOG_MINUTES later and is saved
  // straight away; the user then moves the end time and duration follows.
  const addTimeLog = async () => {
    const startTime = dayjs().second(0).millisecond(0);
    const endTime = startTime.add(DEFAULT_TIME_LOG_MINUTES, 'minute');
    const duration = parseFloat((DEFAULT_TIME_LOG_MINUTES / 60).toFixed(2));
    // Matched by a temp key rather than identity: an edit typed while the POST
    // is out replaces the row object via mergeRow.
    const tempId = `new-${Date.now()}`;
    const draft = {
      _tempId: tempId,
      start_time: startTime.format('YYYY-MM-DDTHH:mm'),
      end_time: endTime.format('YYYY-MM-DDTHH:mm'),
      duration,
      notes: '',
      _saving: true,
    };
    setTimeLogs((prev) => [...prev, draft]);
    try {
      const response = await api.post(`/time-logs/`, {
        action_item: actionItemId,
        start_time: startTime.toISOString(),
        end_time: endTime.toISOString(),
        duration,
        notes: '',
        attendee: localStorage.getItem('user'),
      });
      setTimeLogs((prev) =>
        prev.map((log) =>
          log._tempId === tempId
            ? { ...log, id: response.data.id, duration: response.data.duration, _saving: false }
            : log
        )
      );
    } catch (error) {
      console.error("Failed to create time log", error);
      setTimeLogs((prev) =>
        prev.map((log) => (log._tempId === tempId ? { ...log, _saving: false } : log))
      );
    }
  };

  // Fetch checklist items
  const fetchChecklist = async () => {
    if (!actionItemId) return;
    setChecklistLoading(true);
    try {
      const response = await api.get(`checklist/`, {params: { action_item_id: actionItemId }});
      setChecklist(response.data.payload);
    } catch (error) {
      console.error('Error fetching checklist:', error);
    } finally {
      setChecklistLoading(false);
    }
  };

  // Toggle checklist item checked state
  const handleChecklistToggle = async (item) => {
    try {
      await api.patch(`checklist/${item.id}`, { completed: !item.completed });
      setChecklist((prev) =>
        prev.map((c) =>
          c.id === item.id ? { ...c, completed: !c.completed } : c
        )
      );
    } catch (error) {
      console.error('Error updating checklist item:', error);
    }
  };

  // Add new checklist item
  const handleAddChecklistItem = async () => {
    if (!newChecklistItem.trim()) return;
    try {
      const response = await api.post('checklist/', {
        action_item: actionItemId,
        description: newChecklistItem,
        seq: checklist.length + 1,
        completed: false,
      });
      setChecklist((prev) => [...prev, response.data.payload]);
      setNewChecklistItem('');
    } catch (error) {
      console.error('Error adding checklist item:', error);
    }
  };

  // Add checklist items from template
  const handleChecklistTemplateSelect = async (template) => {
    console.log("Selected template:", template);
    if (!template?.items) return;
    try {
      await Promise.all(
        template.items.map((item) =>
          api.post('checklist/', {
            action_item: actionItemId,
            description: item.description,
            seq: item.seq,
            completed: false,
          })
        )
      );
      fetchChecklist();
    } catch (error) {
      console.error('Error applying checklist template:', error);
    }
    setChecklistTemplateDialogOpen(false);
  };

  return (
    <>
      <HalfScreenDialog
        open={open}
        onClose={onClose}
        aria-labelledby="action-item-dialog-title"
      >
        <DialogHeader>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="h6" id="action-item-dialog-title">
              Action Item Details
            </Typography>
            <FormItem item size={2}>
              <TextField
                label="Created At"
                value={
                  actionItem?.created_at
                    ? dayjs(actionItem.created_at).format("YYYY-MM-DD HH:mm")
                    : ""
                }
                variant="outlined"
                disabled
              />
            </FormItem>
            <FormItem item size={2}>
              <TextField
                label="Updated At"
                value={
                  actionItem?.updated_at
                    ? dayjs(actionItem.updated_at).format("YYYY-MM-DD HH:mm")
                    : ""
                }
                variant="outlined"
                disabled
              />
            </FormItem>
          </Box>
          <IconButton onClick={onClose} size="small">
            <Close fontSize="small" />
          </IconButton>
        </DialogHeader>
        <DialogContent sx={{ p: 3 }}>
          <Paper elevation={0}>
            <Grid container spacing={2}>
              <FormItem item xs={12}>
                <DropZone
                  isDragging={isDragging}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {images.length > 0 && (
                    <ImageGrid>
                      {images.map(image => (
                        <Thumbnail key={image.id}>
                          <img
                            src={MEDIA_URL + image.image}
                            alt="Attachment"
                            crossOrigin="use-credentials"
                            onClick={() => setPreviewUrl(MEDIA_URL + image.image)}
                          />
                          <CloseButton
                            size="small"
                            sx={{ top: 4, right: 4, padding: '2px' }}
                            onClick={() => handleDeleteImage(image.id)}
                          >
                            <Close fontSize="small" />
                          </CloseButton>
                        </Thumbnail>
                      ))}
                    </ImageGrid>
                  )}

                  {uploadingImage && (
                    <UploadOverlay>
                      <CircularProgress color="secondary" />
                    </UploadOverlay>
                  )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<AddPhotoAlternate />}
                    >
                      Add Images
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={handleFileChange}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      or drag &amp; drop, or paste from the clipboard
                    </Typography>
                  </Box>
                </DropZone>
              </FormItem>
              <FormItem item size={12}>
                <TextField
                  label="Description"
                  value={actionItem?.description || ""}
                  onChange={(e) =>
                    handleActionItemChange({ description: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={2}
                  variant="outlined"
                />
              </FormItem>
              <FormItem item size={3}>
                <Box style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                  <TextField
                    type="number"
                    label="Time Estimate (hours)"
                    value={actionItem?.ai_time_estimate === null || actionItem?.ai_time_estimate === undefined ? '' : actionItem.ai_time_estimate}
                    onChange={(e) => {
                      const val = e.target.value;
                      handleActionItemChange({
                        ai_time_estimate: val === '' ? null : parseFloat(val) || 0,
                      });
                    }}
                    fullWidth
                    variant="outlined"
                    inputProps={{ step: "0.01" }}
                  />
                  <TextField
                    type="number"
                    label="Sequence"
                    value={actionItem?.seq || ""}
                    onChange={(e) =>
                      handleActionItemChange({
                        seq: parseInt(e.target.value) || 0,
                      })
                    }
                    fullWidth
                    variant="outlined"
                  />
                </Box>
              </FormItem>
              <FormItem item size={9}>
                <TextField
                  label="Notes"
                  value={actionItem?.notes || ""}
                  onChange={(e) =>
                    handleActionItemChange({ notes: e.target.value })
                  }
                  fullWidth
                  multiline
                  rows={4}
                  variant="outlined"
                />
              </FormItem>
            </Grid>
            {/* Checklist Section */}
            <Box mt={3} mb={3}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Checklist</Typography>
                <Button
                  variant="outlined"
                  onClick={() => setChecklistTemplateDialogOpen(true)}
                  color="primary"
                  size="small"
                >
                  Select checklist template
                </Button>
              </Box>
              <Box>
                {checklistLoading ? (
                  <Typography variant="body2">Loading...</Typography>
                ) : (
                  <List>
                    {checklist.map((item) => (
                      <ListItem
                        key={item.id}
                        dense
                        disableGutters
                        sx={{ pl: 0 }}
                        secondaryAction={null}
                      >
                        <Checkbox
                          checked={!!item.completed}
                          onChange={() => handleChecklistToggle(item)}
                        />
                        <ListItemText
                          primary={item.description}
                          sx={{
                            textDecoration: item.completed ? 'line-through' : 'none',
                            color: item.completed ? 'text.secondary' : 'inherit',
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {guestCaps.canManageChecklists && (
                  <Box display="flex" gap={1} mt={1}>
                    <TextField
                      size="small"
                      variant="outlined"
                      placeholder="Add checklist item"
                      value={newChecklistItem}
                      onChange={(e) => setNewChecklistItem(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddChecklistItem();
                      }}
                      fullWidth
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddChecklistItem}
                    >
                      Add
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
            <Typography variant="h6" gutterBottom>
              Comments
            </Typography>
            <CommentBox>
              <CommentList ref={commentListRef}>
                {comments.map((comment) => (
                  <CommentItem
                    key={comment.id}
                    elevation={1}
                    isCurrentUser={
                      String(comment.commented_by.id).trim() ===
                      String(currentUser).trim()
                    }
                  >
                    <CommentHeader>
                      <Typography
                        variant="caption"
                        color="secondary"
                        sx={{ fontWeight: "bold" }}
                      >
                        {String(comment.commented_by.id).trim() ===
                        String(currentUser).trim()
                          ? "You"
                          : comment.commented_by.full_name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {dayjs(comment.created_at).format("HH:mm")}
                      </Typography>
                    </CommentHeader>
                    {comment.reply_to && (
                      <ReplyIndicator>
                        Replying to:{" "}
                        {comments
                          .find((c) => c.id === comment.reply_to)
                          ?.comment.substring(0, 10)}
                        {comments.find((c) => c.id === comment.reply_to)
                          ?.comment.length > 10
                          ? "..."
                          : ""}
                      </ReplyIndicator>
                    )}
                    <CommentContent>{comment.comment}</CommentContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 0.5,
                      }}
                    >
                      <Button
                        size="small"
                        onClick={() => setReplyTo(comment.id)}
                        sx={{
                          minWidth: "auto",
                          padding: "0 4px",
                          fontSize: "0.75rem",
                          color: "text.secondary",
                        }}
                      >
                        Reply
                      </Button>
                    </Box>
                  </CommentItem>
                ))}
              </CommentList>
              {replyTo && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="textSecondary">
                    Replying to:{" "}
                    {comments
                      .find((c) => c.id === replyTo)
                      ?.comment.substring(0, 30)}
                    {comments.find((c) => c.id === replyTo)?.comment.length > 30
                      ? "..."
                      : ""}
                  </Typography>
                  <Button size="small" onClick={() => setReplyTo(null)}>
                    Cancel Reply
                  </Button>
                </Box>
              )}
              {guestCaps.canComment && (
              <CommentInput>
                <TextField
                  fullWidth
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder={
                    replyTo ? "Write a reply..." : "Write a comment..."
                  }
                  ref={commentInputRef}
                  multiline
                  rows={2}
                  variant="outlined"
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    position: "relative",
                  }}
                >
                  <Button
                    ref={emojiButtonRef}
                    variant="contained"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    size="small"
                  >
                    😊
                  </Button>
                  {showEmojiPicker && (
                    <Box
                      ref={emojiPickerRef}
                      sx={{
                        position: "absolute",
                        zIndex: 1000,
                        right: "50px",
                        bottom: "100px",
                        backgroundColor:
                          theme.palette.mode === "dark"
                            ? theme.palette.grey[800]
                            : theme.palette.grey[100],
                      }}
                    >
                      <EmojiPicker
                        onEmojiClick={(emoji) => {
                          onEmojiClick(emoji);
                          setShowEmojiPicker(false);
                        }}
                      />
                    </Box>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleCommentSubmit}
                    color="primary"
                  >
                    {replyTo ? "Reply" : "Comment"}
                  </Button>
                </Box>
              </CommentInput>
              )}
            </CommentBox>
            <Box mt={2}>
              <Typography variant="h6">Time Logs</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "100px" }}>Start</TableCell>
                      <TableCell sx={{ width: "100px" }}>End</TableCell>
                      <TableCell sx={{ width: "70px" }}>
                        Duration
                      </TableCell>
                      <TableCell sx={{ width: "auto" }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {timeLogs.map((log, index) => (
                      <TableRow key={index}>
                        <TableCell sx={{ padding: "5px 10px", width: "100px" }}>
                          <TextField
                            type="datetime-local"
                            value={
                              log.start_time
                                ? dayjs(log.start_time).format(
                                    "YYYY-MM-DD HH:mm"
                                  )
                                : ""
                            }
                            onChange={(e) =>
                              handleTimeLogChange(
                                index,
                                "start_time",
                                e.target.value
                              )
                            }
                            fullWidth
                            slotProps={{
                              step: 600,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "5px 10px", width: "100px" }}>
                          <TextField
                            type="datetime-local"
                            value={
                              log.end_time
                                ? dayjs(log.end_time).format("YYYY-MM-DD HH:mm")
                                : ""
                            }
                            onChange={(e) =>
                              handleTimeLogChange(
                                index,
                                "end_time",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "5px 10px", width: "70px" }}>
                          <TextField 
                            value={formatFloatToTimeHM(log.duration)}
                            disabled 
                            fullWidth 
                            inputProps={{ style: { textAlign: 'right' } }}
                          />
                        </TableCell>
                        <TableCell sx={{ padding: "5px 10px", width: "auto" }}>
                          <TextField
                            type="text"
                            value={log.notes || ""}
                            onChange={(e) =>
                              handleTimeLogChange(
                                index,
                                "notes",
                                e.target.value
                              )
                            }
                            fullWidth
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button
                onClick={addTimeLog}
                variant="contained"
                color="secondary"
                sx={{ mt: 2 }}
              >
                Add Time Log
              </Button>
            </Box>
          </Paper>
        </DialogContent>
      </HalfScreenDialog>
      {previewUrl && (
        <ImagePreviewModal
          open={Boolean(previewUrl)}
          onClose={() => setPreviewUrl(null)}
          imageUrl={previewUrl}
        />
      )}
      <ChecklistTemplateDialog
        open={checklistTemplateDialogOpen}
        onClose={() => setChecklistTemplateDialogOpen(false)}
        onSelect={handleChecklistTemplateSelect}
      />
    </>
  );
};

export default ActionItemDialog;
